import { Survey } from './survey';
import { Question } from './question';
import { NavigationRule } from './navigation-rule';

export class Page {
  id: number;
  url: string;
  name: string;
  description: string;
  name_pretty: string;
  description_pretty: string;
  order: number;
  survey_id: number;
  key: string;
  script: string;
  template: string;
  is_last: boolean;

  private _survey: Survey;


  getSurvey(): Survey {
    return this._survey;
  }

  setSurvey(survey: Survey) {
    this._survey = survey;
  }

  private questions: any;
  private _questions: Question[] = [];

  getQuestions(): Question[] {
    return this._questions;
  }

  setQuestions(questions: Question[]) {
    this._questions = questions;
  }

  insertQuestion(question: Question) { // question.order contains the order
    this._questions.splice(question.order, 0, question);
    for (var i: number = question.order + 1; i < this._questions.length; i++) {
      this._questions[i].order++;
    }
  }

  deleteQuestion(question: Question) {
    let new_questions = [];
    let question_was_deleted: boolean = false;
    for (let aquestion of this.getQuestions()) {
      if (aquestion.id == question.id)
        question_was_deleted = true;
      else
        new_questions.push(aquestion);
      if (question_was_deleted)
        aquestion.order -= 1;
    }
    this._questions = new_questions;
  }

  isOnlyOneQuestion(): boolean {
    return this.getQuestions().length == 1;
  }

  moveQuestion(question: Question, new_order: number): void {
    let current_order = question.order;

    if (new_order == null)
      new_order = this._questions.length;

    if (new_order == this._questions.length) {
      question.order = this._questions.length - 1;
      this._questions.splice(current_order, 1);
      for (var i = current_order; i < this._questions.length; i++)
        this._questions[i].order = i;
      this._questions.push(question);
    } else if (current_order == new_order) {
    } else {
      this._questions.splice(current_order, 1);
      this._questions.splice(new_order, 0, question);
      if (new_order < current_order) {
        for (var i = new_order; i < this._questions.length; i++)
          this._questions[i].order = i;
      } else {
        for (var i = current_order; i < this._questions.length; i++)
          this._questions[i].order = i;
      }
    }
  }

  private _grouped_questions_map: any = {};

  public getGroupedQuestionsMap(): any {
    return this._grouped_questions_map;
  }

  private navigation_rules: any;
  private _navigation_rules: NavigationRule[];

  getNavigationRules(): NavigationRule[] {
    return this._navigation_rules;
  }

  setNavigationRules(navigation_rules: NavigationRule[]) {
    this._navigation_rules = navigation_rules;
  }


  insertNavigationRule(navigation_rule: NavigationRule) { // navigation_rule.order contains the order
    this._navigation_rules.splice(navigation_rule.order, 0, navigation_rule);
    for (var i: number = navigation_rule.order + 1; i < this._navigation_rules.length; i++) {
      this._navigation_rules[i].order++;
    }
  }

  deleteNavigationRule(navigation_rule: NavigationRule) {
    let new_navigation_rules = [];
    let navigation_rule_was_deleted: boolean = false;
    for (let anavigation_rule of this.getNavigationRules()) {
      if (anavigation_rule.id == navigation_rule.id)
        navigation_rule_was_deleted = true;
      else
        new_navigation_rules.push(anavigation_rule);
      if (navigation_rule_was_deleted)
        anavigation_rule.order -= 1;
    }
    this._navigation_rules = new_navigation_rules;
  }

  isOnlyOneNavigationRule(): boolean {
    return this.getNavigationRules().length == 1;
  }

  moveNavigationRule(navigation_rule: NavigationRule, new_order: number): void {
    let current_order = navigation_rule.order;

    if (new_order == null)
      new_order = this._navigation_rules.length;

    if (new_order == this._navigation_rules.length) {
      navigation_rule.order = this._navigation_rules.length - 1;
      this._navigation_rules.splice(current_order, 1);
      for (var i = current_order; i < this._navigation_rules.length; i++)
        this._navigation_rules[i].order = i;
      this._navigation_rules.push(navigation_rule);
    } else if (current_order == new_order) {
    } else {
      this._navigation_rules.splice(current_order, 1);
      this._navigation_rules.splice(new_order, 0, navigation_rule);
      if (new_order < current_order) {
        for (var i = new_order; i < this._navigation_rules.length; i++)
          this._navigation_rules[i].order = i;
      } else {
        for (var i = current_order; i < this._navigation_rules.length; i++)
          this._navigation_rules[i].order = i;
      }
    }
  }

  isFirstPage(): boolean {
    return this.order == 0;
  }

  isLastPage(): boolean {
    return this.order == (this.getSurvey().getPages().length - 1);
  }

  private _current_question: Question = null;

  setCurrentQuestion(question: Question) {
    this._current_question = question;
  }

  getCurrentQuestion(): Question {
    return this._current_question;
  }

  toJSON(): Object {
    // create an instance of the Page class
    let page = Object.create(Page.prototype);
    // copy all the fields to the json object
    page.key = this.key;
    page.script = this.script;
    page.template = this.template;

    return page;
  }

  static fromJSON(page_json: Object): Page {

    // create an instance of the Page class
    let page = Object.create(Page.prototype);
    // copy all the fields from the json object
    let obj = Object.assign(page, page_json, {
      // convert fields that need converting
      // created: new Date(json.created),
    });

    let questions_json = page.questions;
    let _questions: Question[] = [];

    for (let question_json of questions_json) {
      let question = Question.fromJSON(question_json);
      _questions.push(question);
    }
    page.setQuestions(_questions);
    delete page.questions;

    // partition grouped questions into groups
    page._grouped_questions_map = {};
    for (let question of page.getQuestions()) {
      let group: string = question.group;
      if (!question.group) {
        continue;
      }
      if (group in page._grouped_questions_map) {
        var grouped_questions: Question[] = page._grouped_questions_map[group];
        grouped_questions.push(question);
        page._grouped_questions_map[group] = grouped_questions;
      } else {
        var grouped_questions: Question[] = [];
        grouped_questions.push(question);
        page._grouped_questions_map[group] = grouped_questions;
      }
    }

    let navigation_rules_json = page.navigation_rules;
    let _navigation_rules: NavigationRule[] = [];
    for (let navigation_rule_json of navigation_rules_json) {
      let navigation_rule = NavigationRule.fromJSON(navigation_rule_json);
      _navigation_rules.push(navigation_rule);
    }
    page.setNavigationRules(_navigation_rules);
    delete page.navigation_rules;

    return page;
  }

}
